import React from 'react';
import { Button, Nav, NavItem, } from 'reactstrap';
import { Link } from 'react-router-dom';
import classnames from 'classnames';
import { keyBy, orderBy, groupBy, fromPairs, isEmpty, sumBy, } from 'lodash';
import { format as formatDate, addHours, } from 'date-fns';
import { TextEncoder, } from 'text-encoding';
import { unparse as unparseCsv, } from 'papaparse';
import fileDownload from 'js-file-download';
import { toast } from 'react-toastify';

import firebase, { functions } from '../firebase';
import { batch, getCollectionData, getAllCollectionDataByChunk, } from '../shared/firebase';
import { idWithTenant, } from '../shared/util';
import { computeInventory, priceTaxTypes, } from '../shared/models/product';
import useCollectionSubscription from './hooks/useCollectionSubscription';
import ExportButton from './ExportButton';
import ImportButton from './ImportButton';
import ProgressButton from './ProgressButton';

const mapMercariProducts = functions.httpsCallable('mapMercariProducts', { timeout: 550000 });
const mapRakutenProducts = functions.httpsCallable('mapRakutenProducts', { timeout: 550000 });
const mapTtsProducts = functions.httpsCallable('mapTtsProducts', { timeout: 550000 });
const tabs = [
  { name: 'products', label: '通常商品', path: 'products' },
  { name: 'amazonProducts', label: 'Amazon商品', path: 'amazonProducts' },
  { name: 'cases', label: 'ケース', path: 'cases' },
  { name: 'sets', label: 'セット品', path: 'sets' },
  { name: 'productMallMappings', label: '商品対応表', path: 'productMallMappings', hidden: _ => !_.tenant.tenantOptionValues?.join('').match(/mercari|shopify|rakuten|yahoo|tts|qoo10/), },
  { name: 'mallProducts', label: 'モール商品', path: 'mallProducts', hidden: _ => !_.tenant.tenantOptionValues?.includes('shopify'), },
];

const db = firebase.firestore();

export default function ProductsTabs(props) {
  const { activeTab, tenant, shippingRequests, } = props;
  const otherLocations = useCollectionSubscription(tenant?.ref.collection('otherLocations').orderBy('createdAt'), [tenant]);
  const extraSellers = useCollectionSubscription(tenant?.ref.collection('extraSellers').orderBy('createdAt'), [tenant]);
  const extraSellersById = keyBy(extraSellers, 'id');
  const rowsForExport = async () => {
    const products = await getAllCollectionDataByChunk(tenant.ref.collection('products').orderBy('createdAt'), 1000);
    const caseProducts = products.filter(_ => _.isCase);
    const caseInventoryItems = caseProducts.flatMap((caseProduct) => {
      const caseGoodInventory = (caseProduct.goodInventoryIds || []).length;
      return (caseProduct.items || []).map(item => ({ productId: item.productId, quantity: caseGoodInventory * item.quantity, }));
    });
    const caseInventoryItemsGroupedByProductId = groupBy(caseInventoryItems, 'productId');
    const caseInventoryByProductId = fromPairs(Object.entries(caseInventoryItemsGroupedByProductId).map(([productId, items]) => [productId, sumBy(items, 'quantity')]));
    return orderBy(products, _ => parseInt(_.id, 10), 'desc').map((product) => {
      const productsById = keyBy(products, 'id');
      const { id, ref, sku, createdAt, isAmazonProduct, isCase, isSet, name, barcodeInfo, itemName, requestsNeko, asin, fnsku, price, priceTaxType, deliveryRoute, items = [], extraSellerId, hides, } = product;
      const { goodInventory, ngInventory, } = computeInventory(product, shippingRequests);
      return {
        商品ID: idWithTenant(ref),
        SKU: sku,
        種別: isSet ? 'セット品' : isCase ? 'ケース' : isAmazonProduct ? 'Amazon商品' : '通常商品',
        登録日: formatDate(createdAt.toDate(), 'yyyy/MM/dd'),
        ...(tenant.tenantOptionValues?.includes('multiSellers') && { ショップ: isAmazonProduct ? (extraSellerId ? extraSellersById[extraSellerId]?.name : 'メイン') : '' }),
        商品名: name,
        JANまたはバーコード情報: barcodeInfo,
        品名: itemName,
        ポスト便希望: requestsNeko && 'はい',
        ASIN: asin,
        FNSKU: fnsku,
        販売価格: price,
        税区分: priceTaxTypes[priceTaxType]?.label,
        内容商品SKU: items.map(_ => productsById[_.productId]?.sku).join(', '),
        内容商品数量: items.map(_ => _.quantity).join(', '),
        在庫: goodInventory,
        販売不可在庫: ngInventory,
        ケース内在庫: caseInventoryByProductId[id] || 0,
        非表示: hides && '非表示',
      };
    });
  };
  const onClickMapMercariProducts = async () => {
    if(!window.confirm('メルカリshops商品のマッピングを実行します。よろしいですか？')) return;

    try {
      await mapMercariProducts({ tenantId: tenant.id, });
      toast.success('マッピングしました');
    } catch(e) {
      console.log(e);
      toast.error('失敗しました');
    }
  };
  const onClickMapRakutenProducts = async () => {
    if(!window.confirm('楽天商品のマッピングを実行します。よろしいですか？')) return;

    try {
      await mapRakutenProducts({ tenantId: tenant.id, });
      toast.success('マッピングしました');
    } catch(e) {
      console.log(e);
      toast.error('失敗しました');
    }
  };
  const onClickMapTtsProducts = async () => {
    if(!window.confirm('TikTok Shop商品のマッピングを実行します。よろしいですか？')) return;

    try {
      await mapTtsProducts({ tenantId: tenant.id, });
      toast.success('マッピングしました');
    } catch(e) {
      console.error('TikTok Shop商品マッピングエラー:', e);
      const errorMessage = e.code === 'internal' && e.message
        ? e.message
        : `失敗しました (${e.code || 'unknown'})`;
      toast.error(errorMessage, { autoClose: 10000 });
    }
  };

  const onClickDownloadOtherLocationSampleCsv = () => {
    const data = [{
      ロケーションキー: otherLocations[0]?.sortKey || 'key1',
      SKU: 'sku_123456',
      数量: 5,
    }];
    const encoder = new TextEncoder('Shift_JIS', { NONSTANDARD_allowLegacyEncoding: true });
    const content = encoder.encode(unparseCsv(data));
    fileDownload(content, 'ロケーション在庫_サンプル.csv');
  };
  const processOtherLocationInventoryRows = async (rows) => {
    const otherLocationKeys = otherLocations.map(_ => _.sortKey);
    const invalidKeys = rows.map(_ => _['ロケーションキー']).filter(_ => _ && !otherLocationKeys.includes(_));
    if(invalidKeys.length > 0) throw new Error(`不明なロケーションキー: ${[...new Set(invalidKeys)].join(', ')}`);
    const products = await getAllCollectionDataByChunk(tenant.ref.collection('products').orderBy('createdAt'), 1000);
    const productsBySku = keyBy(products, 'sku');
    const invalidSkus = rows.map(_ => _['SKU']).filter(_ => _ && !productsBySku[_]);
    if(invalidSkus.length > 0) throw new Error(`不明なSKU: ${[...new Set(invalidSkus)].join(', ')}`);
    // セット品は実在庫を持たないためロケーション在庫を設定させない
    const setSkus = rows.map(_ => _['SKU']).filter(_ => productsBySku[_]?.isSet);
    if(setSkus.length > 0) throw new Error(`セット品にはロケーション在庫を設定できません: ${[...new Set(setSkus)].join(', ')}`);
    const rowsGroupedBySku = groupBy(rows, 'SKU');
    await batch(db, Object.entries(rowsGroupedBySku), (b, [sku, skuRows]) => {
      const product = productsBySku[sku];
      const otherLocationInventories = fromPairs(skuRows.map(_ => [_['ロケーションキー'], parseInt(_['数量'], 10) || 0]));
      b.update(product.ref, { otherLocationInventories });
    });
  };

  return (
    <div className="mb-3 d-flex justify-content-between gap-3">
      <Nav tabs className="flex-fill">
        {
          tabs.map(({ label, name, path, hidden = _ => false, }) => {
            return !hidden({ tenant }) && (
              <NavItem key={name}>
                <Link className={classnames('nav-link cursor-pointer', { active: activeTab === name })} to={`/${path}`}>
                  {label}
                </Link>
              </NavItem>
            )
          })
        }
      </Nav>
      <div className="d-flex justify-content-end align-items-end gap-1">
        {
          tenant.tenantOptionValues?.includes('tts') && (
            <ProgressButton process={onClickMapTtsProducts}>
              TikTok Shop商品マッピング
            </ProgressButton>
          )
        }
        <ExportButton label="商品エクスポート" fileName="商品.csv" rows={rowsForExport} />
        {
          otherLocations.length > 0 && (
            <Button onClick={onClickDownloadOtherLocationSampleCsv}>
              <span className="fas fa-download mr-1" />
              ロケーション在庫サンプルCSV
            </Button>
          )
        }
        {
          otherLocations.length > 0 && (
            <ImportButton label="ロケーション在庫インポート" processRows={processOtherLocationInventoryRows} />
          )
        }
      </div>
    </div>
  );
};
